import { useSyncExternalStore } from 'react';

/**
 * Desktop alerts for Messages: the pop-up, the sound and the tab-title count
 * (brief-desktop-alerts.md, decisions D1 to D7).
 *
 * Everything here is browser-only and per device. No table, no RPC: the
 * alerts ride on the unread polls that already exist (my_room_unread every
 * 60s, which keeps running in a hidden tab because it sets
 * refetchIntervalInBackground, see hooks.ts). The Inbox polls every 5 minutes and
 * only in a front tab; when a room's unread goes up, useMessageAlerts
 * refreshes the Inbox at once, so an @ pops within about a minute even in a
 * hidden tab. React Query otherwise skips an interval tick
 * while document.visibilityState is 'hidden' (query-core 5.101.4,
 * queryObserver.ts #updateRefetchInterval and focusManager.isFocused).
 *
 * The pure rules are exported on their own so they have tests without a
 * browser: which rooms went up, what the title says, whether a pop may fire.
 */

/* ── Tab title (D4) ─────────────────────────────────────────────────────── */

/**
 * "(3) TAP Creative Operations Hub", or the plain title when there is
 * nothing unread. The count is the sidebar's Messages badge text, so the two
 * can never disagree ("99+" included).
 */
export function titleWithCount(baseTitle: string, badge: string | null): string {
  return badge === null ? baseTitle : `(${badge}) ${baseTitle}`;
}

/* ── Which rooms went up (D2) ───────────────────────────────────────────── */

/**
 * Rooms whose unread count is higher now than at the previous poll. Nothing
 * on the first load (previous undefined): a pop is for an arrival, not for
 * what was already waiting. A room that is new to the map counts as up from
 * zero (a DM someone just started with me).
 */
export function roomsThatWentUp(
  previous: Map<string, number> | undefined,
  next: Map<string, number> | undefined,
): string[] {
  if (previous === undefined || next === undefined) {
    return [];
  }
  const out: string[] = [];
  for (const [roomId, count] of next) {
    if (count > (previous.get(roomId) ?? 0)) {
      out.push(roomId);
    }
  }
  return out;
}

/* ── The quiet room (D3) ────────────────────────────────────────────────── */

/**
 * A room whose new messages count in the badge and the title but never pop
 * or sound unless they @mention me (the mention then pops from the Inbox row).
 *
 * The Agents room is recognised by its shape, not its name: a channel that
 * is members_only. Today that is only Agents (20270176000100:10, "today only
 * the Agents room"); every DM and group is members_only too but is not a
 * channel. If another private channel is ever made, it will be quiet as well,
 * which is the safe way round.
 */
export function isQuietRoom(room: { kind: string; membersOnly?: boolean } | undefined): boolean {
  return room !== undefined && room.kind === 'channel' && room.membersOnly === true;
}

/**
 * The hook point for per-room mute (brief: "muted rooms never alert"). No
 * room mute exists on 3eb95b5 (only task_mutes, 20270184000100:687-690), so
 * nothing is muted. When a room mute ships, this is the one line that reads it.
 */
export function isMutedRoom(roomId: string): boolean {
  void roomId;
  return false;
}

/* ── Which Inbox rows pop ───────────────────────────────────────────────── */

/**
 * Inbox events that are "a message for me" in Messages: an @ in a room, DM,
 * group or thread (mentioned), a reply in a thread I started or replied in
 * (thread_reply; my_room_unread does not count thread replies, so this is the
 * only way one can pop), and a new comment from Alex in the Alex Huddle chat
 * (huddle_comment). All only when the row is marked as Messages
 * (source = 'messages', npc r3), so an @ on a project task never pops here.
 */
export const POP_EVENTS: ReadonlySet<string> = new Set([
  'mentioned',
  'thread_reply',
  'huddle_comment',
]);

/* ── May a pop fire at all ──────────────────────────────────────────────── */

export type AlertPermission = 'unsupported' | 'default' | 'granted' | 'denied';

export function alertPermission(): AlertPermission {
  if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
    return 'unsupported';
  }
  return window.Notification.permission;
}

/** The Hub tab is the thing the person is looking at right now. */
export function pageHasFocus(): boolean {
  if (typeof document === 'undefined') {
    return true;
  }
  return document.visibilityState === 'visible' && document.hasFocus();
}

/* ── One tab alerts, not every open Hub tab ─────────────────────────────── */

/**
 * Two Hub tabs both poll and would both pop and both play the sound. The tab
 * the person used last is the one that alerts: it writes its id here on
 * focus. A tab that closes clears the key if it held it, and while the key is
 * empty every tab may alert (the Notification tag, the comment id, then
 * folds the pop-ups into one; only the sound could repeat).
 */
export const LEADER_KEY = 'tap.messages.alertTab';

const TAB_ID = `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

export function thisTabId(): string {
  return TAB_ID;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // Private mode or storage off: this device just behaves as one tab.
  }
}

export function claimAlertTab(tabId: string = TAB_ID): void {
  writeStorage(LEADER_KEY, tabId);
}

export function releaseAlertTab(tabId: string = TAB_ID): void {
  if (readStorage(LEADER_KEY) === tabId) {
    writeStorage(LEADER_KEY, null);
  }
}

export function isAlertTab(tabId: string = TAB_ID): boolean {
  const leader = readStorage(LEADER_KEY);
  return leader === null || leader === tabId;
}

/* ── Sound preference (D5) ──────────────────────────────────────────────── */

/** Per device, like the theme and the text size. On unless set to 'off'. */
export const SOUND_KEY = 'tap.messages.sound';

const soundListeners = new Set<() => void>();

export function soundOn(): boolean {
  return readStorage(SOUND_KEY) !== 'off';
}

export function setSoundOn(on: boolean): void {
  writeStorage(SOUND_KEY, on ? 'on' : 'off');
  soundListeners.forEach((listener) => listener());
}

function subscribeSound(listener: () => void): () => void {
  soundListeners.add(listener);
  return () => {
    soundListeners.delete(listener);
  };
}

export function useSoundOn(): boolean {
  return useSyncExternalStore(subscribeSound, soundOn, () => true);
}

/* ── Permission (D6) ────────────────────────────────────────────────────── */

const permissionListeners = new Set<() => void>();

function subscribePermission(listener: () => void): () => void {
  permissionListeners.add(listener);
  return () => {
    permissionListeners.delete(listener);
  };
}

export function usePermission(): AlertPermission {
  return useSyncExternalStore(subscribePermission, alertPermission, () => 'unsupported');
}

/**
 * Asks the browser. Only ever called from the "Turn on desktop alerts" button,
 * never on load. Older Safari takes a callback and returns nothing; every
 * current browser returns a promise. Both are handled.
 */
export async function requestAlertPermission(): Promise<AlertPermission> {
  if (alertPermission() === 'unsupported') {
    return 'unsupported';
  }
  // The same click unlocks the sound, so the first chime in a background tab
  // is not refused by the autoplay rule.
  unlockSound();
  const result = await new Promise<NotificationPermission>((resolve) => {
    const maybe = window.Notification.requestPermission(resolve);
    if (maybe !== undefined && typeof maybe.then === 'function') {
      void maybe.then(resolve, () => resolve(window.Notification.permission));
    }
  });
  permissionListeners.forEach((listener) => listener());
  return result;
}

/* ── The chime ──────────────────────────────────────────────────────────── */

/**
 * Made with Web Audio, not a file: nothing to host, nothing to license. Two
 * soft sine notes, about a third of a second, quiet (peak gain 0.08).
 *
 * Browsers refuse audio that no click ever allowed. The AudioContext is
 * created or resumed on the first pointer press anywhere in the Hub (and on
 * the alerts button); after that, a chime in a hidden tab plays. If it is
 * still refused, it is silent: no error, no retry.
 */
let audio: AudioContext | null = null;

function audioContextClass(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

export function unlockSound(): void {
  const Ctor = audioContextClass();
  if (Ctor === undefined) {
    return;
  }
  try {
    if (audio === null) {
      audio = new Ctor();
    }
    if (audio.state === 'suspended') {
      void audio.resume().catch(() => undefined);
    }
  } catch {
    audio = null;
  }
}

export function playChime(): void {
  if (audio === null || audio.state !== 'running') {
    return;
  }
  try {
    const now = audio.currentTime;
    for (const [offset, frequency] of [
      [0, 880],
      [0.14, 1320],
    ] as const) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0, now + offset);
      gain.gain.linearRampToValueAtTime(0.08, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.22);
      osc.connect(gain).connect(audio.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.25);
    }
  } catch {
    // Silent on purpose.
  }
}

/* ── The pop-up (D7) ────────────────────────────────────────────────────── */

export interface AlertContent {
  /** The comment id: the Notification tag, so the same message folds into one. */
  tag: string;
  title: string;
  body: string;
  /** In-app path to open on click. */
  href: string;
}

/**
 * Shows one pop-up. Click: bring the tab forward, close the pop-up, open the
 * message. Returns false when the browser would not show it.
 */
export function showAlert(content: AlertContent, open: (href: string) => void): boolean {
  if (alertPermission() !== 'granted') {
    return false;
  }
  try {
    const note = new window.Notification(content.title, {
      body: content.body,
      tag: content.tag,
      icon: '/4ocean-logo-blue.png',
    });
    note.onclick = () => {
      window.focus();
      note.close();
      open(content.href);
    };
    return true;
  } catch {
    // Chrome on Android refuses the constructor (service worker only).
    return false;
  }
}

/** First line of a message, short enough for a pop-up. */
export function firstLine(text: string, max = 120): string {
  const line = text.split('\n').find((part) => part.trim() !== '') ?? '';
  const trimmed = line.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}
